import React, { useCallback, useEffect, useState } from 'react';
import { getRepositories } from '../../services/storage';
import type { SyncAccion } from '../../services/storage/interfaces/ISyncAccionesRepository';
import { useRepositoryChanges } from '../../hooks/useRepositoryChanges';

/**
 * SyncAccionesIndicator - Indicador de acciones fallidas (server-side)
 *
 * Spec: docs/superpowers/specs/2026-09-25-alta-masiva-sin-caravana-design.md (§5.5)
 *
 * SOLO muestra acciones que el server rechazó al ejecutar (estado='fallida')
 * con su motivo — es lo único relevante post-push:
 *   - pendientes de ENVIAR → ya las cubre el QueueViewer (pending_changes)
 *   - ejecutadas → norma, sin noticias es buena noticia
 * El badge está ausente cuando no hay fallidas.
 */

const etiquetaAccion = (accion: SyncAccion): string => {
  if (accion.tipo === 'reasignar_historial') {
    const p = accion.payload;
    return p?.session_code
      ? `Corregir caravana ${p.caravana_origen} → ${p.caravana_destino} (sesión)`
      : `Asignar caravana real ${p.caravana_origen} → ${p.caravana_destino}`;
  }
  return accion.tipo;
};

const SyncAccionesIndicator: React.FC = () => {
  const [acciones, setAcciones] = useState<SyncAccion[]>([]);
  const [open, setOpen] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { syncAcciones } = getRepositories();
      setAcciones(await syncAcciones.getFallidas());
    } catch {
      // sin factory / sin sesión: sin acciones que mostrar
      setAcciones([]);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  // Reactivo: recarga cuando cambia la tabla local (encolado o pull)
  const { lastEvent } = useRepositoryChanges({ table: 'sync_acciones' });
  useEffect(() => {
    if (lastEvent) void cargar();
  }, [lastEvent, cargar]);

  if (acciones.length === 0 && !open) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white transition-colors bg-danger/60 hover:bg-danger/80"
        title={`${acciones.length} acción(es) que el servidor no pudo ejecutar — clic para ver el motivo`}
        aria-label={`Acciones fallidas: ${acciones.length}`}
        data-ar-guide="header-sync-acciones"
      >
        <i className="bi bi-exclamation-triangle text-[10px]" />
        <span>{acciones.length}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Acciones fallidas"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="font-bold text-ink">Acciones que el servidor no pudo ejecutar</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600" aria-label="Cerrar">
                <i className="bi bi-x-lg" />
              </button>
            </div>
            <div className="px-5 py-3 overflow-y-auto text-sm space-y-2">
              {acciones.length === 0 && (
                <p className="text-slate-500">No hay acciones fallidas.</p>
              )}
              {acciones.map(a => (
                <div
                  key={a.id}
                  className="rounded-lg border border-danger/40 bg-danger/5 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-ink">{etiquetaAccion(a)}</span>
                    <span className="text-xs font-semibold text-danger">Fallida</span>
                  </div>
                  {a.error && (
                    <p className="mt-1 text-xs text-danger">{a.error}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="px-5 py-3 border-t border-gray-200 text-xs text-slate-500">
              Las acciones fallidas no se reintentan solas: revisá el motivo y volvé a hacer la operación.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default SyncAccionesIndicator;
